import React, { useEffect, useState } from "react";

/**
 * Premium session splash — elegant luxury/fintech reveal.
 *
 * Sequence (≈4.6s total):
 *   0.0s — Deep navy scene fades in with a subtle radial gold vignette
 *   0.2s — Twin gold hairlines sweep out from center
 *   0.4s — "CREDIT QUACK" wordmark draws itself via SVG stroke-dashoffset
 *   1.6s — Gold shimmer sweep glides across the traced wordmark
 *   1.8s — Filled solid wordmark fades in on top of the traced outline
 *   2.2s — Tagline lifts in
 *   4.1s — Everything fades to reveal the app
 *
 * Respects prefers-reduced-motion.
 */
export default function SplashScreen({ onDone }) {
  const [show, setShow] = useState(true);
  const [phase, setPhase] = useState("in"); // in → out

  useEffect(() => {
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    if (reduced) {
      setShow(false);
      onDone?.();
      return;
    }
    const t1 = setTimeout(() => setPhase("out"), 4100);
    const t2 = setTimeout(() => { setShow(false); onDone?.(); }, 4600);
    return () => { clearTimeout(t1); clearTimeout(t2); };
    // eslint-disable-next-line
  }, []);

  if (!show) return null;

  return (
    <div
      className={`cq-lux-splash fixed inset-0 z-[100] flex items-center justify-center overflow-hidden ${phase === "out" ? "cq-lux-out" : ""}`}
      aria-hidden="true"
      data-testid="splash-screen"
    >
      {/* Deep obsidian background with warm gold vignette */}
      <div className="absolute inset-0 bg-[#08070d]" />
      <div className="absolute inset-0 cq-lux-vignette" />
      <div className="absolute inset-0 cq-lux-grain" />

      {/* Twin hairlines */}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[92vw] max-w-[720px] pointer-events-none">
        <div className="cq-lux-line cq-lux-line-top" />
        <div className="cq-lux-line cq-lux-line-bot" />
      </div>

      {/* Constellation particles */}
      <div className="absolute inset-0 pointer-events-none">
        {[...Array(28)].map((_, i) => (
          <span
            key={i}
            className="cq-lux-star"
            style={{
              left: `${(i * 37 + 11) % 100}%`,
              top: `${(i * 61 + 7) % 100}%`,
              animationDelay: `${(i % 10) * 0.24}s`,
              animationDuration: `${2.4 + (i % 5) * 0.35}s`,
            }}
          />
        ))}
      </div>

      {/* Wordmark stage */}
      <div className="relative z-10 flex flex-col items-center px-6">
        <div className="relative">
          {/* Traced (outlined) wordmark — draws itself */}
          <svg
            className="cq-lux-word cq-lux-word-trace block"
            viewBox="0 0 720 120"
            width="min(88vw, 720px)"
            height="auto"
            aria-hidden="true"
          >
            <defs>
              <linearGradient id="cqGold" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%"  stopColor="#f5d06b" />
                <stop offset="45%" stopColor="#fef3c7" />
                <stop offset="55%" stopColor="#fef3c7" />
                <stop offset="100%" stopColor="#c9962b" />
              </linearGradient>
              <linearGradient id="cqShimmer" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%"   stopColor="rgba(255,255,255,0)" />
                <stop offset="45%"  stopColor="rgba(255,255,255,0.9)" />
                <stop offset="55%"  stopColor="rgba(255,255,255,0.9)" />
                <stop offset="100%" stopColor="rgba(255,255,255,0)" />
              </linearGradient>
              <clipPath id="cqWordClip">
                <text
                  x="360" y="82"
                  textAnchor="middle"
                  fontFamily="'Playfair Display', 'Cormorant Garamond', 'Georgia', serif"
                  fontSize="82"
                  fontWeight="900"
                  letterSpacing="6"
                >CREDIT QUACK</text>
              </clipPath>
            </defs>
            <text
              x="360" y="82"
              textAnchor="middle"
              fontFamily="'Playfair Display', 'Cormorant Garamond', 'Georgia', serif"
              fontSize="82"
              fontWeight="900"
              letterSpacing="6"
              fill="none"
              stroke="url(#cqGold)"
              strokeWidth="1.3"
            >CREDIT QUACK</text>
          </svg>

          {/* Filled wordmark — fades in on top of the trace */}
          <svg
            className="cq-lux-word cq-lux-word-fill absolute inset-0"
            viewBox="0 0 720 120"
            width="min(88vw, 720px)"
            height="auto"
            aria-hidden="true"
          >
            <text
              x="360" y="82"
              textAnchor="middle"
              fontFamily="'Playfair Display', 'Cormorant Garamond', 'Georgia', serif"
              fontSize="82"
              fontWeight="900"
              letterSpacing="6"
              fill="url(#cqGold)"
            >CREDIT QUACK</text>
          </svg>

          {/* Shimmer sweep — clipped to wordmark shape */}
          <svg
            className="cq-lux-word cq-lux-shimmer absolute inset-0"
            viewBox="0 0 720 120"
            width="min(88vw, 720px)"
            height="auto"
            aria-hidden="true"
          >
            <g clipPath="url(#cqWordClip)">
              <rect className="cq-lux-shimmer-bar" x="-260" y="0" width="220" height="120" fill="url(#cqShimmer)" />
            </g>
          </svg>
        </div>

        {/* Divider crest with duck flourish */}
        <div className="cq-lux-crest mt-6 flex items-center gap-4">
          <span className="cq-lux-rule" />
          <svg width="34" height="34" viewBox="0 0 40 40" aria-hidden="true" className="cq-lux-duck">
            <defs>
              <linearGradient id="duckGold" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#f5d06b" />
                <stop offset="100%" stopColor="#c9962b" />
              </linearGradient>
            </defs>
            {/* Simplified elegant duck silhouette */}
            <path
              d="M8 24 C 8 18, 14 14, 20 14 C 24 14, 27 15, 29 17 L 33 15 L 31 20 C 32 22, 32 24, 31 26 C 30 29, 26 31, 21 31 L 12 31 L 10 34 L 9 31 C 8 30, 7.5 27, 8 24 Z"
              fill="url(#duckGold)"
            />
            <circle cx="25" cy="20" r="1.1" fill="#0b0a10" />
          </svg>
          <span className="cq-lux-rule" />
        </div>

        <p className="cq-lux-tag mt-5 tracking-[0.42em] uppercase text-[10px] sm:text-xs font-semibold text-amber-100/70">
          Chasing&nbsp;·&nbsp;Better&nbsp;·&nbsp;Credit
        </p>

        <div className="cq-lux-loading mt-8 flex items-center gap-1.5">
          <span></span><span></span><span></span>
        </div>
      </div>

      <style>{`
        .cq-lux-splash { animation: cq-lux-fade-in 480ms ease-out both; }
        .cq-lux-out    { animation: cq-lux-fade-out 500ms cubic-bezier(0.65,0,0.35,1) both; }
        @keyframes cq-lux-fade-in  { from { opacity: 0 } to { opacity: 1 } }
        @keyframes cq-lux-fade-out { to   { opacity: 0; filter: blur(6px); transform: scale(1.02) } }

        .cq-lux-vignette {
          background:
            radial-gradient(60% 55% at 50% 50%, rgba(217,168,66,0.28) 0%, rgba(120,88,30,0.10) 35%, rgba(8,7,13,0) 70%),
            radial-gradient(120% 90% at 50% 120%, rgba(201,150,43,0.18) 0%, rgba(8,7,13,0) 55%);
          animation: cq-lux-vignette-pulse 5.6s ease-in-out infinite alternate;
        }
        @keyframes cq-lux-vignette-pulse {
          0%   { opacity: 0.85; transform: scale(1); }
          100% { opacity: 1;    transform: scale(1.04); }
        }
        .cq-lux-grain {
          background-image:
            radial-gradient(rgba(255,255,255,0.045) 1px, transparent 1px),
            radial-gradient(rgba(0,0,0,0.32) 1px, transparent 1px);
          background-size: 3px 3px, 5px 5px;
          background-position: 0 0, 1px 2px;
          mix-blend-mode: overlay;
          opacity: 0.55;
          pointer-events: none;
        }

        .cq-lux-line {
          position: absolute;
          left: 50%;
          height: 1px;
          width: 0;
          background: linear-gradient(90deg, transparent 0%, #f5d06b 45%, #fef3c7 50%, #c9962b 55%, transparent 100%);
          transform: translateX(-50%);
          box-shadow: 0 0 12px rgba(245,208,107,0.55);
          animation: cq-lux-line-grow 900ms cubic-bezier(0.22,1,0.36,1) 200ms forwards;
        }
        .cq-lux-line-top { top: -72px; }
        .cq-lux-line-bot { top:  72px; }
        @keyframes cq-lux-line-grow {
          from { width: 0; opacity: 0 }
          to   { width: 100%; opacity: 1 }
        }

        .cq-lux-star {
          position: absolute;
          height: 3px; width: 3px;
          border-radius: 999px;
          background: #fef3c7;
          box-shadow: 0 0 8px rgba(245,208,107,0.9), 0 0 16px rgba(245,208,107,0.35);
          opacity: 0;
          animation: cq-lux-star-twinkle 3s ease-in-out infinite;
        }
        @keyframes cq-lux-star-twinkle {
          0%,100% { opacity: 0; transform: scale(0.6); }
          40%     { opacity: 0.9; transform: scale(1.1); }
          60%     { opacity: 0.6; transform: scale(1); }
        }

        /* Wordmark tracing */
        .cq-lux-word { max-width: 100%; height: auto; overflow: visible; }
        .cq-lux-word-trace text {
          stroke-dasharray: 2200;
          stroke-dashoffset: 2200;
          animation: cq-lux-trace 1.6s cubic-bezier(0.22,1,0.36,1) 400ms forwards;
          filter: drop-shadow(0 0 6px rgba(245,208,107,0.35));
        }
        @keyframes cq-lux-trace {
          to { stroke-dashoffset: 0; }
        }
        .cq-lux-word-fill {
          opacity: 0;
          animation: cq-lux-fill-in 900ms ease-out 1.8s forwards;
        }
        .cq-lux-word-fill text {
          filter: drop-shadow(0 4px 18px rgba(201,150,43,0.4));
        }
        @keyframes cq-lux-fill-in { to { opacity: 1 } }

        .cq-lux-shimmer .cq-lux-shimmer-bar {
          animation: cq-lux-shimmer-sweep 1.5s cubic-bezier(0.65,0,0.35,1) 1.6s;
        }
        @keyframes cq-lux-shimmer-sweep {
          0%   { transform: translateX(0);     opacity: 0; }
          15%  { opacity: 1; }
          85%  { opacity: 1; }
          100% { transform: translateX(940px); opacity: 0; }
        }

        .cq-lux-crest { opacity: 0; animation: cq-lux-crest-in 700ms ease-out 2.05s forwards; }
        @keyframes cq-lux-crest-in { to { opacity: 1 } }
        .cq-lux-rule {
          display: inline-block;
          height: 1px; width: 110px;
          background: linear-gradient(90deg, transparent, rgba(245,208,107,0.85), transparent);
        }
        .cq-lux-duck {
          filter: drop-shadow(0 2px 6px rgba(245,208,107,0.45));
          animation: cq-lux-duck-bob 2.2s ease-in-out 2.1s infinite;
        }
        @keyframes cq-lux-duck-bob {
          0%,100% { transform: translateY(0) rotate(-2deg); }
          50%     { transform: translateY(-3px) rotate(2deg); }
        }

        .cq-lux-tag {
          opacity: 0;
          transform: translateY(6px);
          animation: cq-lux-tag-in 800ms ease-out 2.2s forwards;
          text-shadow: 0 1px 4px rgba(0,0,0,0.6);
        }
        @keyframes cq-lux-tag-in { to { opacity: 1; transform: translateY(0); } }

        .cq-lux-loading {
          opacity: 0;
          animation: cq-lux-loading-in 500ms ease 2.6s forwards;
        }
        .cq-lux-loading span {
          height: 5px; width: 5px;
          border-radius: 999px;
          background: #f5d06b;
          box-shadow: 0 0 8px rgba(245,208,107,0.9);
          animation: cq-lux-dot 1.2s ease-in-out infinite;
        }
        .cq-lux-loading span:nth-child(2) { animation-delay: 0.15s }
        .cq-lux-loading span:nth-child(3) { animation-delay: 0.3s }
        @keyframes cq-lux-loading-in { to { opacity: 1 } }
        @keyframes cq-lux-dot {
          0%,100% { transform: scale(0.7); opacity: 0.35 }
          50%     { transform: scale(1.2); opacity: 1 }
        }
      `}</style>
    </div>
  );
}
